import { useEffect, useState } from "react";
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, Divider, Paper,
  Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { consultantsApi, type ConsultantDto } from "@/api/endpoints";
import { errorMessage } from "@/api/client";
import { ClosableDialogTitle } from "@/components/ClosableDialogTitle";
import { StatCard } from "@/components/StatCard";
import { formatDate } from "@/shared/datetime";

const inr = new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" });

/** First and last day of the month containing `d` — the period a payout run almost always covers. */
function monthBounds(d = new Date()) {
  const first = new Date(d.getFullYear(), d.getMonth(), 1);
  const last = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  const iso = (x: Date) =>
    `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`;
  return { from: iso(first), to: iso(last) };
}

/**
 * One consultant's statement for a period (W20).
 *
 * <p>Every figure here is what was recorded on the line when the bill was raised, not what today's
 * rules would produce. A statement that re-derived history would change every time an arrangement
 * was renegotiated — including for months already paid — and the first person to notice would be
 * the consultant whose last cheque no longer reconciles.</p>
 *
 * <p>The effective percentage is the number a renegotiation actually starts from: what they took
 * across the whole period, whatever mix of rules produced it.</p>
 */
export function ConsultantPayoutDialog({
  consultant, onClose,
}: { consultant: ConsultantDto | null; onClose: () => void }) {
  const [fromDate, setFromDate] = useState(monthBounds().from);
  const [toDate, setToDate] = useState(monthBounds().to);

  useEffect(() => {
    if (!consultant) return;
    const b = monthBounds();
    setFromDate(b.from);
    setToDate(b.to);
  }, [consultant]);

  const q = useQuery({
    queryKey: ["consultant-payout", consultant?.id, fromDate, toDate],
    queryFn: () => consultantsApi.payout(consultant!.id, fromDate, toDate),
    enabled: !!consultant && !!fromDate && !!toDate,
  });

  const r = q.data;

  return (
    <Dialog open={!!consultant} onClose={onClose} maxWidth="lg" fullWidth>
      <ClosableDialogTitle onClose={onClose}>
        Payout statement — {consultant?.name}
      </ClosableDialogTitle>
      <DialogContent dividers>
        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
          <TextField type="date" label="From" size="small" InputLabelProps={{ shrink: true }}
            value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
          <TextField type="date" label="To" size="small" InputLabelProps={{ shrink: true }}
            value={toDate} onChange={(e) => setToDate(e.target.value)}
            helperText="Inclusive" />
        </Stack>

        {q.isLoading && (
          <Stack alignItems="center" sx={{ py: 4 }}><CircularProgress size={24} /></Stack>
        )}
        {q.isError && <Alert severity="error">{errorMessage(q.error)}</Alert>}

        {r && (
          <>
            <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: "wrap" }}>
              <StatCard label="Payable" value={inr.format(r.share)} severity="info"
                hint={`${r.billCount} bill${r.billCount === 1 ? "" : "s"}`} />
              <StatCard label="Billed on their lines" value={inr.format(r.billed)}
                hint="after discounts" />
              <StatCard label="Effective share" value={`${r.effectivePct}%`}
                hint="across the whole period" />
              <StatCard label="Lines" value={r.lineCount} />
            </Stack>

            {r.lineCount === 0 && (
              <Alert severity="info">
                Nothing earned in this period. Only posted bills count — a draft has earned nobody
                anything, and a cancelled one earned nobody anything either.
              </Alert>
            )}

            {r.lineCount > 0 && (
              <>
                <Typography variant="overline" color="text.secondary">By service</Typography>
                <Paper variant="outlined" sx={{ p: 1.5, mt: 1, mb: 2 }}>
                  <Stack spacing={0.5}>
                    {r.byService.map(s => (
                      <Stack key={s.serviceName} direction="row" justifyContent="space-between">
                        <Typography variant="body2">
                          {s.serviceName}
                          <Typography component="span" variant="caption" color="text.secondary"
                            sx={{ ml: 1 }}>
                            ×{s.lines}
                          </Typography>
                        </Typography>
                        <Typography variant="body2">
                          {inr.format(s.share)}
                          <Typography component="span" variant="caption" color="text.secondary"
                            sx={{ ml: 1 }}>
                            of {inr.format(s.billed)}
                          </Typography>
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>
                </Paper>

                <Divider sx={{ mb: 1 }} />
                <Typography variant="overline" color="text.secondary">Line by line</Typography>
                <TableContainer sx={{ maxHeight: 360, mt: 1 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ width: 140 }}>Bill</TableCell>
                        <TableCell sx={{ width: 110 }}>Date</TableCell>
                        <TableCell>Service</TableCell>
                        <TableCell>Patient</TableCell>
                        <TableCell align="right" sx={{ width: 110 }}>Line net</TableCell>
                        <TableCell align="right" sx={{ width: 110 }}>Share</TableCell>
                        <TableCell sx={{ width: 170 }}>Basis</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {r.lines.map((l, i) => (
                        <TableRow key={`${l.invoiceId}-${i}`} hover>
                          <TableCell>{l.documentNo}</TableCell>
                          <TableCell>{formatDate(l.documentDate)}</TableCell>
                          <TableCell>{l.serviceName}</TableCell>
                          <TableCell>
                            {l.patientName ?? "—"}
                            {l.uhid && (
                              <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                                {l.uhid}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="right">{inr.format(l.lineNet)}</TableCell>
                          <TableCell align="right">{inr.format(l.share)}</TableCell>
                          <TableCell>
                            <Typography variant="caption" color="text.secondary">
                              {l.rule ?? "—"}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Each share is what was recorded when the bill was raised. Renegotiating a rule
                    does not restate a period already paid.
                  </Typography>
                </Box>
              </>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
